import { Browser } from "../../core/browser";
import { Razor } from "../../razor/razor";
import { AuxiliumComponent } from "../auxilium-component";

export class Button extends AuxiliumComponent {
	#Mappers = {};
	#OriginalIcon = "";

	static observedAttributes = ["icon", "button-style", "variation", "size", "value", "orientation", "link", "active", "suffix-icon", "button-class", "description", "icon-colour", "loading"];

	constructor() {
		super();

		this.loading = this.hasAttribute("loading");
		this.icon = this.getAttribute("icon") ?? "";
		this.iconColour = this.getAttribute("icon-colour") ?? "";
		this.variation = this.getAttribute("variation") ?? "theme";
		this.description = this.getAttribute("description") ?? "";
		this.buttonStyle = this.getAttribute("button-style") ?? "";
		this.buttonClass = this.getAttribute("button-class") ?? "";
		this.identifier = this.getAttribute("identifier") ?? crypto.randomUUID().slice(0, 8);
		this.size = this.getAttribute("size") ?? "";
		this.active = this.hasAttribute("active");
		this.suffixIcon = this.getAttribute("suffix-icon");
		this.value = this.getAttribute("value");
		this.link = this.getAttribute("link") ?? "";
		this.submits = this.hasAttribute("submits");
		this.orientation = this.getAttribute("orientation") ?? "";
		this.stepDirection = this.getAttribute("step-direction") ?? "";

		this.#Mappers = {
			form: this.getAttribute("form") ?? "",
			autofocus: this.hasAttribute("focus"),
			download: this.getAttribute("download") ?? "",
			type: this.getAttribute("type") ?? "button",
			target: this.getAttribute("target") ?? "",
			formaction: this.getAttribute("formaction") ?? "",
			formmethod: this.getAttribute("formmethod") ?? ""
		};

		Object.keys(this.#Mappers).forEach(key => this[key] = this.#Mappers[key]);

		if (this.hasAttribute("onclick")) {
			this.role = "button";
		}
	}

	get textContent() {
		return this.querySelector("span")?.textContent ?? "";
	}

	set textContent(value) {
		const span = this.querySelector("span");

		if (span) {
			span.textContent = value;
		}
	}

	#SetDescription(description, button) {
		if (!button) { return; }

		if (button.nodeName === "BUTTON") {
			if (!button.id) {
				button.id = this.identifier;
			}

			let label = this.querySelector(":scope > label.visually-hidden");

			if (!label) {
				const newLabel = document.createElementWithContents("label", null, { className: "visually-hidden" });
				this.prepend(newLabel);

				label = newLabel;
			}

			label.textContent = description;
			label.htmlFor = button.id;
		} else {
			button.ariaLabel = description;
		}
	}

	parsedCallback() {
		if (this.variation === "theme" && Browser.GetFlavour() === "gov" && !this.suffixIcon) {
			this.suffixIcon = "fas fa-chevron-right";
		}

		const button = document.createElementWithContents(this.link ? "a" : "button", this.childNodes.length ? document.createElementWithContents("span", this.childNodes) : null, {
			className: "btn--base"
		});

		button.onfocus = ev => {
			ev.halt();
			this.raiseEvent("focus");
		}

		button.classList.add(Button.ClassFromVariation(this.variation))

		if (this.link && this.#Mappers.target === "_blank") {
			button.appendChild(document.createElementWithContents("span", "(opens in new window)", {
				className: "visually-hidden"
			}));
		}

		if (this.identifier) { button.id = this.identifier; }
		if (this.stepDirection) { button.addEventListener("click", () => this.#HandleStepButtonClick()); }
		if (this.active) { button.classList.add("active"); }
		if (this.buttonClass) { button.classList.add(...this.buttonClass.split(" ")); }

		if (this.tooltip && button.textContent.trim() === "") { this.#SetDescription(this.tooltip, button); }
		if (this.description && !button.textContent?.trim()) { this.#SetDescription(this.description, button); }

		if (this.submits) {
			button.classList.add("btn-entity-post");
			button.onclick = () => Razor.FireButtonAjax(button);
		}

		if (this.link) { button.href = Browser.ResolveRelativeURL(this.link); }
		if (this.size) { button.classList.add(`btn--${this.size.toLowerCase()}`); }
		if (this.buttonStyle) { button.classList.add(`btn--${this.buttonStyle.toLowerCase()}`); }
		if (this.orientation) { button.classList.add(`btn--${this.orientation.toLowerCase()}`); }

		if (this.icon) {
			const icon = document.createElementWithContents("chip-icon", null, {
				icon: this.icon
			});

			if (this.iconColour) {
				icon.colour = this.iconColour;
			}

			button.prepend(icon);
		}

		if (this.suffixIcon) {
			button.appendChild(document.createElementWithContents("chip-icon", null, {
				icon: this.suffixIcon
			}));
		}

		Object.keys(this.#Mappers).filter(x => this.#Mappers[x])
			.forEach(key => button.setAttribute(key, this.#Mappers[key] === true ? "" : this.#Mappers[key]));

		this.#OriginalIcon = this.icon;
		this.#updateLoadingState(this.loading);

		this.appendChild(button);
	}

	#HandleStepButtonClick() {
		const tabContent = this.closest(".tab-group__tab");

		let index = [...this.closest(".tab-group__content")?.children ?? []].indexOf(tabContent);

		if (this.stepDirection.toLowerCase() === "next") { index = Math.min(index + 1, this.closest(".tab-group__content").children.length - 1); }
		else { index = Math.max(index - 1, 0); }

		const tab = this.closest("chip-tabgroup")?.querySelector(`:scope > .tab-group__tabs > .tab__wrapper > chip-tab:nth-child(${index + 1})`);

		if (tab && (this.closest("chip-tabgroup")?.querySelector(`:scope > .tab-group__tabs > .tab__wrapper > chip-tab:nth-child(${index})`)?.checkValidity() === true || this.stepDirection.toLowerCase() !== "next")) {
			tab.selected = true;
		}
	}

	click() {
		this.querySelector(":scope > button, :scope > a")?.click();
	}

	#updateLoadingState(isLoading) {
		if (isLoading) {
			this.#OriginalIcon = this.icon;
			this.icon = "fad fa-spinner-third spin";
		} else {
			this.icon = this.#OriginalIcon;
		}

		this.disabled = isLoading;
	}

	#updateIcon(icon) {
		const existingIcon = this.querySelector(":scope>.btn--base>chip-icon:first-child");

		if (!icon?.trim()) { return existingIcon?.remove(); }

		if (existingIcon) {
			existingIcon.icon = icon;
		} else {
			this.querySelector(":scope>.btn--base")?.prepend(document.createElementWithContents("chip-icon", null, { icon: icon }));
		}
	}

	attributeChanged(name, oldValue, newValue) {
		switch (name) {
			case "variation":
				this.querySelectorAll(":scope>button, :scope>a, :scope>chip-dropdown>div>button").forEach(x => {
					x.classList.remove(Button.ClassFromVariation(oldValue));
					x.classList.add(Button.ClassFromVariation(newValue));
				});
				break;
			case "size":
				this.querySelectorAll(":scope>button, :scope>a, :scope>chip-dropdown>div>button").forEach(x => {
					x.classList.remove(`btn--${this.size}`);

					if (newValue) {
						x.classList.add(`btn--${newValue.toLowerCase()}`);
					}
				});
				break;
			case "button-style":
				this.querySelectorAll(":scope>button, :scope>a, :scope>chip-dropdown>div>button").forEach(x => {
					x.classList.remove(`btn--${oldValue}`);

					if (newValue) {
						x.classList.add(`btn--${newValue.toLowerCase()}`);
					}
				});
				break;
			case "button-class":
				{
					const buttons = this.querySelectorAll(":scope>button, :scope>a, :scope>chip-dropdown>div>button");

					if (oldValue) { buttons.forEach(x => x.classList.remove(...oldValue.split(" "))); }
					if (newValue) { buttons.forEach(x => x.classList.add(...newValue.split(" "))); }
				}
				break;
			case "orientation":
				this.firstElementChild.classList.remove(`btn--${this.orientation}`);
				this.firstElementChild.classList.add(`btn--${newValue.toLowerCase()}`);
				break;
			case "tooltip":
				{
					const target = this.querySelector("button, a");

					if (target?.textContent.trim() === "") {
						this.#SetDescription(newValue, target);
					}
				}
				break;
			case "identifier":
				{
					const target = this.querySelector("button, a");

					if (target) {
						target.id = newValue;
					}
				}
				break;
			case "description":
				{
					const target = this.querySelector("button, a");

					if (target && !target.textContent?.trim()) {
						this.#SetDescription(newValue, target);
					}
				}
				break;
			case "icon":
				this.#updateIcon(newValue);
				break;
			case "icon-colour":
				{
					const existingIcon = this.querySelector("chip-icon");

					if (existingIcon) {
						existingIcon.classList.add("text-theme");
						existingIcon.dataset.colour = newValue;
					} else {
						this.prepend(document.createElementWithContents("chip-icon", null, { icon: newValue }));
					}
				}
				break;
			case "suffix-icon":
				{
					const existingIcon = this.querySelector(":scope > div > button > i:last-child:not(:first-child)");

					if (existingIcon) {
						existingIcon.className = newValue;
					} else {
						this.querySelector(":scope > div > button")?.appendChild(document.createElementWithContents("chip-icon", null, { icon: newValue }));
					}
				}
				break;
			case "link":
				{
					const anchor = this.querySelector("a");

					if (anchor) {
						if (newValue) {
							anchor.href = Browser.ResolveRelativeURL(newValue);
						} else {
							anchor.removeAttribute("href");
						}
					}
				}
				break;
			case "disabled":
				this.querySelectorAll(":scope>button, :scope>a, :scope>chip-dropdown>div>button").forEach(x => {
					if (this.IsAttributeValueTrue(newValue)) {
						x.disabled = true;
					} else {
						x.removeAttribute("disabled");
					}
				});
				break;
			case "active":
				if (this.IsAttributeValueTrue(newValue)) {
					this.querySelector("button, a").classList.add("active");
				} else {
					this.querySelector("button, a").classList.remove("active");
				}
				break;
			case "loading":
				this.#updateLoadingState(this.IsAttributeValueTrue(newValue));
				break;
		}
	}

	focus() {
		const attempt = this.querySelector("a, button");

		if (attempt) {
			attempt.focus();
		} else {
			this.onparsed = () => this.querySelector("a, button")?.focus();
		}
	}

	static ClassFromVariation(variation) {
		switch (variation?.toLowerCase()) {
			case "theme-outline":
			case "info-outline":
			case "success-outline":
			case "warning-outline":
			case "secondary-outline":
			case "danger-outline":
			case "theme-secondary":
			case "theme-tertiary":
			case "secondary-secondary":
			case "danger-secondary":
			case "danger-tertiary":
			case "warning-secondary":
			case "warning-tertiary":
			case "secondary-tertiary":
			case "success-secondary":
			case "info":
			case "info-secondary":
			case "info-tertiary":
			case "body":
			case "success-tertiary":
			case "pagination":
			case "secondary":
			case "none":
			case "sidebar":
			case "theme": return `btn-${variation.toLowerCase()}`;

			case "success":
			case "warning":
			case "danger": return `btn-${variation.toLowerCase()}-action`;
			default: return "btn-theme";
		}
	}
}